import { AppConstants } from '@shared/core';
import { Dayjs } from '@shared/utils';
import { Observable, map } from 'rxjs';
import { BookingServiceBase } from '../client/base-booking-service-client.service';
import { BsFreeSlotsRequestDto, TimeSlotDto } from '../dto';
import { TimeSlotBookingMapper } from '../mapper';
import { BookingDetailsModel, TimeSlotMetadata, TimeSlotResultModel } from '../model';
export class TimeslotRequestCombinationsGenerator {
    constructor(private appConstants: AppConstants) {}

    public generateValidCombinations(
        contractorUuids: string[],
        subcontractorUuids: string[],
        locationUuids: string[],
        bookingInfo: BookingDetailsModel,
    ): {
        contractorUuid: string;
        locationUuid?: string;
        subcontractorUuid: string;
        serviceUuid: string;
        serviceName: string;
        orgUnitUuids: string[];
    }[] {
        const combinations = [];

        // Iterate over selected contractors
        for (const contractorUuid of contractorUuids) {
            // Get the valid subcontractors for this contractor
            const contractorSubcontractors = bookingInfo?.subcontractors.subcontractors.filter(
                subcontractor => subcontractor.contractorUuid === contractorUuid && subcontractorUuids.includes(subcontractor.uuid),
            );

            // For each subcontractor, iterate over their services
            for (const subcontractor of contractorSubcontractors || []) {
                const services = subcontractor.services || [];

                // For each service of this subcontractor, we need to validate the combination
                for (const service of services) {
                    // Get the contractor's locations
                    const contractorLocations = bookingInfo?.locations?.locations?.filter(location =>
                        location.contractors.some(contractor => contractor.uuid === contractorUuid),
                    );

                    // For each valid location, create a combination
                    for (const location of contractorLocations || []) {
                        if (locationUuids.includes(location.uuid)) {
                            // Add orgUnitUuids from the contractor's location
                            const orgUnitUuids = location.contractors
                                .filter(contractor => contractor.uuid === contractorUuid)
                                .flatMap(contractor => contractor.orgUnitUuids);

                            // If we have valid orgUnitUuids, add the combination
                            if (orgUnitUuids.length > 0) {
                                combinations.push({
                                    contractorUuid,
                                    locationUuid: location.uuid,
                                    subcontractorUuid: subcontractor.uuid,
                                    serviceUuid: service.uuid,
                                    serviceName: service.name || '/',
                                    orgUnitUuids,
                                });
                            }
                        }
                    }
                    // TODO - MD-3804 find a comment explaining the challenge with multiple constractor services
                    // Right now, we expect that there is only one contractor service mapped to the BS service
                    // break;
                }
            }
        }

        return combinations;
    }

    public generateTimeslotRequests(
        validCombinations: {
            contractorUuid: string;
            locationUuid?: string;
            subcontractorUuid: string;
            serviceUuid: string;
            serviceName: string;
            orgUnitUuids: string[];
        }[],
        fromDate: Dayjs,
        toDate: Dayjs | undefined,
        bookingInfo: BookingDetailsModel,
        selfBookingServiceClientService: BookingServiceBase,
    ): Observable<TimeSlotResultModel>[] {
        return validCombinations.flatMap(combination => {
            const dataDto: BsFreeSlotsRequestDto = {
                contractorUuid: combination.contractorUuid,
                subcontractorUuid: combination.subcontractorUuid,
                serviceUuid: combination.serviceUuid,
                serviceName: combination.serviceName,
                orgUnitUuids: combination.orgUnitUuids?.join(','),
                firstAvailable: false,
                numSlots: this.appConstants.numOfFreeSlotsPerRequest,
                from: fromDate.format(this.appConstants.defaultDateFormat),
                to: toDate ? toDate.format(this.appConstants.defaultDateFormat) : undefined,
            };

            return selfBookingServiceClientService
                .getFreeSlots(bookingInfo.bookingAllowanceinfo?.bsId as number, bookingInfo.bookingAllowanceinfo?.allowancetoken as string, dataDto)
                .pipe(map(responseDto => this.mapResponseToTimeSlotResult(responseDto, combination, bookingInfo)));
        });
    }

    private mapResponseToTimeSlotResult(
        responseDto: TimeSlotDto[],
        combination: {
            contractorUuid: string;
            locationUuid?: string;
            subcontractorUuid: string;
            serviceUuid: string;
            serviceName: string;
            orgUnitUuids: string[];
        },
        bookingInfo: BookingDetailsModel, // Assuming this is passed in for additional details
    ): TimeSlotResultModel {
        const timeslots = responseDto?.map(slotDto => new TimeSlotBookingMapper().mapDtoToModel(slotDto));

        const contractor = bookingInfo.contractors.contractors.find(contractor => contractor.uuid === combination.contractorUuid);
        const subcontractor = bookingInfo.subcontractors.subcontractors.find(subcontractor => subcontractor.uuid === combination.subcontractorUuid);
        const location = combination.locationUuid
            ? bookingInfo.locations?.locations?.find(location => location.uuid === combination.locationUuid)
            : undefined;

        const metadata: TimeSlotMetadata = {
            contractorUuid: combination.contractorUuid,
            contractorName: contractor?.name,
            contractorLocation:
                contractor?.address && contractor?.postNumber && contractor?.post
                    ? `${contractor.address} ${contractor.postNumber}, ${contractor.post}`
                    : undefined,
            subcontractorUuid: combination.subcontractorUuid,
            subcontractorName: subcontractor?.name,
            contractorServiceUuid: combination.serviceUuid,
            contractorServiceName: combination.serviceName,
            locationUuid: combination.locationUuid,
            locationInfo: location?.address,
        };

        return {
            timeslots,
            metadata,
        };
    }
}
